import type { BoggleAchievementDefinition } from '../types'

export const launchAchievementDefinitions = [
  {
    category: 'career',
    description: 'Find words across qualifying runs.',
    id: 'word-hoard',
    tiers: [
      {
        description: 'Find 100 total words.',
        id: 'word-hoard-100',
        label: '100',
        requirement: { type: 'total_words', count: 100 },
      },
      {
        description: 'Find 500 total words.',
        id: 'word-hoard-500',
        label: '500',
        requirement: { type: 'total_words', count: 500 },
      },
      {
        description: 'Find 1,000 total words.',
        id: 'word-hoard-1000',
        label: '1K',
        requirement: { type: 'total_words', count: 1000 },
      },
      {
        description: 'Find 2,500 total words.',
        id: 'word-hoard-2500',
        label: '2.5K',
        requirement: { type: 'total_words', count: 2500 },
      },
      {
        description: 'Find 5,000 total words.',
        id: 'word-hoard-5000',
        label: '5K',
        requirement: { type: 'total_words', count: 5000 },
      },
    ],
    title: 'Word Hoard',
    visibility: 'visible',
  },
  {
    category: 'career',
    description: 'Build a lifetime shelf of long words.',
    id: 'deep-cut',
    tiers: [
      {
        description: 'Find 50 words with 7 or more letters.',
        id: 'deep-cut-7',
        label: '50x7',
        requirement: { type: 'long_words', count: 50, minLength: 7 },
      },
      {
        description: 'Find 25 words with 8 or more letters.',
        id: 'deep-cut-8',
        label: '25x8',
        requirement: { type: 'long_words', count: 25, minLength: 8 },
      },
      {
        description: 'Find 10 words with 9 or more letters.',
        id: 'deep-cut-9',
        label: '10x9',
        requirement: { type: 'long_words', count: 10, minLength: 9 },
      },
    ],
    title: 'Deep Cut',
    visibility: 'visible',
  },
  {
    category: 'career',
    description: 'Complete qualifying runs.',
    id: 'run-veteran',
    tiers: [
      {
        description: 'Complete 10 qualifying runs.',
        id: 'run-veteran-10',
        label: '10',
        requirement: { type: 'completed_runs', count: 10 },
      },
      {
        description: 'Complete 50 qualifying runs.',
        id: 'run-veteran-50',
        label: '50',
        requirement: { type: 'completed_runs', count: 50 },
      },
      {
        description: 'Complete 100 qualifying runs.',
        id: 'run-veteran-100',
        label: '100',
        requirement: { type: 'completed_runs', count: 100 },
      },
    ],
    title: 'Run Veteran',
    visibility: 'visible',
  },
  {
    category: 'ratings',
    description: 'Complete runs at each known rating.',
    id: 'contender',
    tiers: [
      {
        description: 'Complete a run at Bronze or better.',
        id: 'contender-bronze',
        label: 'Bronze',
        requirement: { type: 'reached_rating', ratingId: 'bronze' },
      },
      {
        description: 'Complete a run at Silver or better.',
        id: 'contender-silver',
        label: 'Silver',
        requirement: { type: 'reached_rating', ratingId: 'silver' },
      },
      {
        description: 'Complete a run at Gold or better.',
        id: 'contender-gold',
        label: 'Gold',
        requirement: { type: 'reached_rating', ratingId: 'gold' },
      },
      {
        description: 'Complete a run at Diamond or better.',
        id: 'contender-diamond',
        label: 'Diamond',
        requirement: { type: 'reached_rating', ratingId: 'diamond' },
        revealRatingId: 'diamond',
        visibility: 'hidden_until_prerequisite',
      },
      {
        description: 'Complete a run at Ace.',
        id: 'contender-ace',
        label: 'Ace',
        requirement: { type: 'reached_rating', ratingId: 'ace' },
        revealRatingId: 'ace',
        visibility: 'hidden_until_prerequisite',
      },
    ],
    title: 'Contender',
    visibility: 'visible',
  },
  {
    category: 'badge-album',
    description: 'Earn the first badge of each rarity.',
    id: 'badge-breakthrough',
    tiers: [
      {
        description: 'Earn your first Common badge.',
        id: 'badge-breakthrough-common',
        label: 'Common',
        requirement: { type: 'first_badge_of_level', level: 'common' },
      },
      {
        description: 'Earn your first Uncommon badge.',
        id: 'badge-breakthrough-uncommon',
        label: 'Uncommon',
        requirement: { type: 'first_badge_of_level', level: 'uncommon' },
      },
      {
        description: 'Earn your first Rare badge.',
        id: 'badge-breakthrough-rare',
        label: 'Rare',
        requirement: { type: 'first_badge_of_level', level: 'rare' },
      },
      {
        description: 'Earn your first Legendary badge.',
        id: 'badge-breakthrough-legendary',
        label: 'Legendary',
        requirement: { type: 'first_badge_of_level', level: 'legendary' },
      },
    ],
    title: 'Badge Breakthrough',
    visibility: 'visible',
  },
  {
    category: 'badge-album',
    description: 'Unlock the full Common badge set.',
    id: 'common-album',
    tiers: [
      {
        description: 'Earn every Common badge.',
        id: 'common-album-all',
        label: 'All',
        requirement: { type: 'all_badges_of_level', level: 'common' },
      },
    ],
    title: 'Common Album',
    visibility: 'visible',
  },
  {
    category: 'feats',
    description: 'Find many words in one qualifying run.',
    id: 'heavy-hitter',
    tiers: [
      {
        description: 'Find 30 words in one run.',
        id: 'heavy-hitter-30',
        label: '30',
        requirement: { type: 'single_run_word_count', count: 30 },
      },
      {
        description: 'Find 40 words in one run.',
        id: 'heavy-hitter-40',
        label: '40',
        requirement: { type: 'single_run_word_count', count: 40 },
      },
      {
        description: 'Find 50 words in one run.',
        id: 'heavy-hitter-50',
        label: '50',
        requirement: { type: 'single_run_word_count', count: 50 },
      },
    ],
    title: 'Heavy Hitter',
    visibility: 'visible',
  },
] as const satisfies readonly BoggleAchievementDefinition[]
